import { Component, EventEmitter, OnDestroy, OnInit, Output, signal } from '@angular/core'
import { Subject, takeUntil, throttleTime } from 'rxjs'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { format } from 'date-fns'
import { Layers } from 'src/app/shared/layers-config/layers'

interface LayerReloadStatus {
  label: string
  minZoom: number
  canReload: boolean
}

@Component({
  selector: 'app-internal-history-control',
  templateUrl: './internal-history-control.component.html',
  standalone: false,
})
export class InternalHistoryControlComponent implements OnInit, OnDestroy {
  @Output() historyClosed = new EventEmitter<void>()
  public appPath = AppPaths.Internal
  public currentDate: Date | undefined
  public isLoading = signal(false)
  public currentZoom = 0
  public reloadStatuses: LayerReloadStatus[] = []
  public nonReloadableStatuses: LayerReloadStatus[] = []
  public showReloadStatuses = false
  private readonly clusteredAndObservationsMinZoom = 14
  private readonly layerMinZoomMap: Record<string, { label: string; minZoom: number }> = {
    [Layers.Provider1Observations.name]: { label: 'Provider 1 Observations', minZoom: 14 },
    [Layers.Provider2Observations.name]: { label: 'Provider 2 Observations', minZoom: 14 },
    [Layers.ClusteredSigns.name]: { label: 'Clustered Signs', minZoom: 14 },
    [Layers.NonDetections.name]: { label: 'Non-Detections', minZoom: 14 },
    [Layers.MatchedSigns.name]: { label: 'Matched Signs', minZoom: 9 },
    [Layers.ConstructionSites.name]: { label: 'Construction Sites', minZoom: 8 },
    [Layers.RolloutsTile.name]: { label: 'Rollouts Tile', minZoom: 5 },
    [Layers.RolloutsMatchedSignsTile.name]: { label: 'Rollouts Matched Signs', minZoom: 7 },
    [Layers.RolloutsFilteredMatchedSignsTile.name]: {
      label: 'Rollouts Filtered Matched Signs',
      minZoom: 7,
    },
  }
  private cleanUp$ = new Subject<void>()

  constructor(private appState: AppStateService) { }

  ngOnInit(): void {
    this.currentDate = this.appState.selectTimestamp() ?? new Date()
    this.currentDate.setHours(0, 0, 0, 0)
    this.currentZoom = this.appState.getState().zoom ?? 0

    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      this.evaluateQueryParameters()
    })

    this.appState.isLoadingChanged$
      .pipe(
        throttleTime(1000, undefined, { leading: true, trailing: true }),
        takeUntil(this.cleanUp$),
      )
      .subscribe((isLoading) => {
        this.isLoading.set(isLoading)
      })

    this.updateQueryParameters()
  }

  ngOnDestroy(): void {
    this.appState.setState(this.appPath, { timestamp: undefined })
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  handleOnTimestampChanged = (timestamp: Date) => {
    this.currentDate = timestamp
    this.currentDate.setHours(0, 0, 0, 0)
    this.updateQueryParameters()
  }

  handleReloadClick = () => {
    this.currentZoom = this.appState.getState().zoom ?? this.currentZoom
    this.reloadStatuses = this.getReloadStatusesByVisibleLayer()
    this.nonReloadableStatuses = this.reloadStatuses.filter((status) => !status.canReload)
    this.showReloadStatuses = this.nonReloadableStatuses.length > 0

    if (!this.currentDate) {
      this.currentDate = this.appState.selectTimestamp() || new Date()
      this.currentDate.setHours(0, 0, 0, 0)
    }
    // Prime previousState so hasTimestampChanged() returns true even when the
    // date has not changed, then fire stateChanged exactly once with the real
    // timestamp. This avoids the previous two-emission pattern (undefined →
    // realDate) that caused layers to rebuild twice and flicker.
    this.appState.setStateSilent({ timestamp: undefined })
    this.updateQueryParameters()
  }

  updateQueryParameters() {
    this.appState.setState(this.appPath, {
      timestamp: this.currentDate
        ? format(this.currentDate, "yyyy-MM-dd'T'HH:mm:ss'Z'")
        : undefined,
    })
  }

  evaluateQueryParameters() {
    this.currentZoom = this.appState.getState().zoom ?? this.currentZoom

    if (this.appState.hasDatasourceParamChanged()) {
      this.updateQueryParameters()
    }
  }

  isBelowObservationAndClusteredMinZoom(): boolean {
    return this.currentZoom < this.clusteredAndObservationsMinZoom
  }

  isObservationOrClusteredLayerVisible(): boolean {
    const activeLayers = this.appState.selectLayers()
    return (
      activeLayers.includes(Layers.ClusteredSigns.name) ||
      activeLayers.includes(Layers.Provider1Observations.name) ||
      activeLayers.includes(Layers.Provider2Observations.name)
    )
  }

  handleOnHistoryClosed = () => {
    this.historyClosed.emit()
  }

  getReloadableCount(): number {
    return this.reloadStatuses.filter((status) => status.canReload).length
  }

  getNonReloadableCount(): number {
    return this.nonReloadableStatuses.length
  }

  private getReloadStatusesByVisibleLayer(): LayerReloadStatus[] {
    const activeLayers = this.appState.selectLayers()

    return activeLayers
      .map((layerKey) => {
        const config = this.layerMinZoomMap[layerKey]

        if (!config) {
          return undefined
        }

        return {
          label: config.label,
          minZoom: config.minZoom,
          canReload: this.currentZoom >= config.minZoom,
        } as LayerReloadStatus
      })
      .filter((status): status is LayerReloadStatus => status !== undefined)
  }
}
